import {useTranslation} from '@aboutyou/i18n';
import {useCanGoBack, useHistory} from '@aboutyou/router';
import {UnstyledButton, UnstyledLinkLinaria} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {ComponentType, FC} from 'react';
import {useAppendFirstProductIdCallback} from '../firstProductId/useFirstProductIds';

export type GoBackViewProps = {
  className?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  Icon?: ComponentType<React.PropsWithChildren>;
  View?: ComponentType<{onClick?: React.MouseEventHandler<HTMLElement>}>;
  addFirstProductId?: boolean;
};

export const GoBackView: FC<
  GoBackViewProps & {backLinkTarget?: {url: string; external: boolean}}
> = ({
  backLinkTarget,
  className,
  style,
  dataTestId,
  addFirstProductId,
  ...rest
}) => {
  const {t} = useTranslation();
  const canGoBack = useCanGoBack();
  const {goBack, push} = useHistory();
  const appendFirstProductIdCallback = useAppendFirstProductIdCallback();
  const ariaLabel = t('Navigate back');

  const Icon = 'Icon' in rest ? rest.Icon! : null;
  const View = 'View' in rest ? rest.View! : null;

  if (canGoBack) {
    return Icon ? (
      <WrapperButton
        className={className}
        style={style}
        data-testid={dataTestId ?? 'backButton_navigation'}
        onClick={goBack}
        aria-label={ariaLabel}
      >
        <Icon />
      </WrapperButton>
    ) : (
      View && (
        <View
          data-testid={dataTestId ?? 'backButton_navigation'}
          onClick={goBack}
        />
      )
    );
  }

  if (backLinkTarget) {
    const [pathname, search] = backLinkTarget.url.split('?');
    const searchWithFirstProductId = appendFirstProductIdCallback(
      new URLSearchParams(search),
    );

    const searchOrEmpty = search ? `?${search}` : '';

    const searchParams =
      addFirstProductId && searchWithFirstProductId
        ? searchWithFirstProductId
        : searchOrEmpty;

    const to = `${pathname}${searchParams}`;

    const goToBackLink = () => {
      if (to) {
        push(to);
      }
    };

    return Icon ? (
      <WrapperLink
        className={className}
        style={style}
        data-testid={dataTestId ?? 'backButton_directEntry'}
        to={to}
        aria-label={ariaLabel}
      >
        <Icon />
      </WrapperLink>
    ) : (
      View && (
        <View
          data-testid={dataTestId ?? 'backButton_directEntry'}
          onClick={goToBackLink}
        />
      )
    );
  }

  if (Icon) {
    return <Icon />;
  }
  if (View) {
    return <View />;
  }

  return null;
};

const buttonStyles = `
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 17px !important;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px auto Highlight;
    outline: 2px auto -webkit-focus-ring-color;
  }
`;

const WrapperLink = styled(UnstyledLinkLinaria)`
  ${buttonStyles}
`;

const WrapperButton = styled(UnstyledButton)`
  ${buttonStyles}
`;
